<template>
  <div class="performance-stats">
    <div class="row">
      <div class="col-md-3">
        <div class="card">
          <div class="card-body">
            <h6 class="card-subtitle mb-2 text-muted">Total Transactions</h6>
            <h4 class="card-title">{{ metrics.total_transactions }}</h4>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card">
          <div class="card-body">
            <h6 class="card-subtitle mb-2 text-muted">With Market Price</h6>
            <h4 class="card-title">
              {{ metrics.transactions_with_market_price }}
            </h4>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card">
          <div class="card-body">
            <h6 class="card-subtitle mb-2 text-muted">Avg Unit Price</h6>
            <h4 class="card-title">
              {{
                metrics.average_unit_price
                  ? `€${parseFloat(metrics.average_unit_price).toFixed(3)}/MWh`
                  : "N/A"
              }}
            </h4>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card">
          <div class="card-body">
            <h6 class="card-subtitle mb-2 text-muted">Avg Market Price</h6>
            <h4 class="card-title">
              {{
                metrics.average_market_price
                  ? `€${parseFloat(metrics.average_market_price).toFixed(3)}/MWh`
                  : "N/A"
              }}
            </h4>
          </div>
        </div>
      </div>
    </div>
    <div class="row mt-3">
      <div class="col-md-6">
        <div class="card">
          <div class="card-body">
            <h6 class="card-subtitle mb-2 text-muted">
              Simple Avg Premium vs Market
            </h6>
            <h4
              class="card-title"
              :class="{
                'text-success': averagePremium > 0,
                'text-danger': averagePremium < 0,
              }"
            >
              {{
                metrics.average_premium
                  ? `${averagePremium >= 0 ? "+" : ""}€${parseFloat(metrics.average_premium).toFixed(3)}/MWh`
                  : "N/A"
              }}
            </h4>
            <small class="text-muted">
              {{ percentageMessage }}
            </small>
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="card">
          <div class="card-body">
            <h6 class="card-subtitle mb-2 text-muted">
              Weighted Avg Premium vs Market
            </h6>
            <h4
              class="card-title"
              :class="{
                'text-success': weightedAveragePremium > 0,
                'text-danger': weightedAveragePremium < 0,
              }"
            >
              {{
                metrics.weighted_average_premium
                  ? `${weightedAveragePremium >= 0 ? "+" : ""}€${parseFloat(metrics.weighted_average_premium).toFixed(3)}/MWh`
                  : "N/A"
              }}
            </h4>
            <small class="text-muted">
              {{ weightedPercentageMessage }}
            </small>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { PerformanceMetrics } from "./transactionPerformanceApi";

interface Props {
  metrics: PerformanceMetrics;
}

const props = defineProps<Props>();

const averagePremium = computed(() => {
  if (!props.metrics.average_premium) {
    return 0;
  }
  return parseFloat(props.metrics.average_premium);
});

const weightedAveragePremium = computed(() => {
  if (!props.metrics.weighted_average_premium) {
    return 0;
  }
  return parseFloat(props.metrics.weighted_average_premium);
});

const formatPercentageMessage = (
  premium: string | null,
  marketPrice: string | null
): string => {
  const parsedMarket = parseFloat(marketPrice ?? "0.0");

  if (premium && Number.isFinite(parsedMarket) && parsedMarket > 0) {
    const parsedPremium = parseFloat(premium);
    const percentage = (parsedPremium / parsedMarket) * 100;
    const absPercentage = Math.abs(percentage);
    const direction = parsedPremium >= 0 ? "above" : "below";
    return `(${absPercentage.toFixed(2)}% ${direction} market)`;
  }

  return "N/A";
};

const percentageMessage = computed(() => {
  return formatPercentageMessage(
    props.metrics.average_premium,
    props.metrics.average_market_price
  );
});

const weightedPercentageMessage = computed(() => {
  return formatPercentageMessage(
    props.metrics.weighted_average_premium,
    props.metrics.weighted_average_market_price
  );
});
</script>

<style scoped>
.performance-stats {
  margin-bottom: 2rem;
}
</style>
